import { createContext, useContext } from "react";

import type { LoadingContextValue } from "./types";

/**
 * Contexte React pour le système de progression de chargement
 *
 * Ce contexte permet aux pages enfants de reporter leur progression
 * au composant parent (PageLoader) qui affiche la barre de progression.
 *
 * @example
 * ```tsx
 * // Dans un composant enfant
 * const { completeStep } = useLoadingProgress();
 * completeStep('init');
 * ```
 */
export const LoadingProgressContext = createContext<LoadingContextValue | null>(
  null,
);

/**
 * Hook pour accéder au contexte de progression
 *
 * ⚠️ IMPORTANT : Ce hook DOIT être utilisé dans un composant enfant de PageLoader
 *
 * @throws Error si utilisé en dehors d'un LoadingProgressProvider
 * @returns Les actions et l'état du système de chargement
 *
 * @example
 * ```tsx
 * function Viewer() {
 *   const { initSteps, completeStep } = useLoadingProgressContext();
 *
 *   useEffect(() => {
 *     initSteps(VIEWER_LOADING_STEPS);
 *   }, []);
 *
 *   // Plus tard, après chaque étape...
 *   completeStep('cesium-init');
 * }
 * ```
 */
export function useLoadingProgressContext(): LoadingContextValue {
  const context = useContext(LoadingProgressContext);

  if (!context) {
    // Message d'erreur détaillé pour aider les développeurs
    console.error(
      `%c[LoadingProgress] ❌ ERREUR: useLoadingProgressContext appelé en dehors du Provider!

📖 SOLUTION:
   Le composant qui utilise ce hook doit être enveloppé dans un <PageLoader>.
   
   Vérifiez que votre page est chargée via React.lazy() dans App.tsx
   et qu'elle est enveloppée dans <Suspense fallback={<PageLoader />}>.

📁 Fichiers concernés:
   - src/App.tsx (configuration des routes)
   - src/components/PageLoader.tsx (provider)
`,
      "color: red; font-weight: bold",
    );

    throw new Error(
      "[LoadingProgress] useLoadingProgressContext doit être utilisé dans un composant enfant de PageLoader",
    );
  }

  return context;
}

/**
 * Hook optionnel qui ne throw pas si le contexte n'existe pas
 * Utile pour les composants qui peuvent fonctionner avec ou sans progression
 */
export function useOptionalLoadingProgress(): LoadingContextValue | null {
  return useContext(LoadingProgressContext);
}
